import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import LittleLensLogo from '../components/LittleLensLogo';
import { presentationBackground } from '../theme/presentationBackground';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(t('forgotPassword.emailRequired'));
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError(t('forgotPassword.emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword({ email });
      setSuccess(response.message || t('forgotPassword.resetEmailSent'));
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('forgotPassword.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('forgotPassword.genericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEmail(newValue);
    if (error) setError(null);
    if (emailError) validateEmail(newValue);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        width: '100vw',
      }}
    >
      <Container maxWidth={false} sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 440, borderRadius: 5, boxShadow: '0 24px 48px rgba(15,23,42,0.16)', border: 'none' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <LittleLensLogo maxWidth={100} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom fontWeight={800} sx={{ color: 'text.primary' }}>
                {t('forgotPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('forgotPassword.subtitle')}
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('forgotPassword.emailLabel')}
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => validateEmail(email)}
                required
                margin="normal"
                error={!!emailError}
                helperText={emailError}
                disabled={loading || !!success}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: 'grey.400' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2.5 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !!emailError || !!success}
                sx={{
                  mb: 3,
                  py: 1.5,
                  fontSize: '0.95rem',
                  background: presentationBackground.sidebarGradient,
                  boxShadow: '0 4px 14px rgba(73, 185, 217, 0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #269BB2 0%, #1F8499 100%)', boxShadow: '0 6px 20px rgba(73, 185, 217, 0.45)', transform: 'translateY(-1px)' },
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : t('forgotPassword.sendResetLink')}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontWeight: 600, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18, mr: 1 }} />
                  {t('forgotPassword.backToSignIn')}
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
